/**
 * PageTour — generic first-run coach-mark overlay reused by any page.
 *
 * Modeled on the existing DashboardTour, but takes its steps + localStorage
 * key as props so each page can define its own walkthrough without
 * duplicating the popover / highlight-ring logic.
 *
 * Usage:
 *   <PageTour
 *     steps={STEPS}
 *     storageKey="learning_tour_seen_v1"
 *     forceOpen={triggerFromButton}
 *     onFinish={() => setTriggerFromButton(false)}
 *   />
 *
 * Each step:
 *   { target: 'css-selector', title: 'Copy', body: 'More copy',
 *     placement: 'top' | 'bottom' }
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { X, ArrowRight, ArrowLeft, Sparkles } from 'lucide-react';

const highlightPad = 8;
const cardWidth = 340;
const cardMargin = 14;

const PageTour = ({ steps, storageKey, forceOpen = false, onFinish, autoStart = true }) => {
  const [visible, setVisible] = useState(false);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [cardPos, setCardPos] = useState({ top: 0, left: 0 });
  const cardRef = useRef(null);

  // Reset index when opened
  useEffect(() => {
    if (visible) setIdx(0);
  }, [visible]);

  // Decide whether to auto-run
  useEffect(() => {
    if (forceOpen) { setVisible(true); return undefined; }
    if (!autoStart) return undefined;
    try {
      const seen = window.localStorage.getItem(storageKey);
      if (!seen) {
        const t = setTimeout(() => setVisible(true), 900);
        return () => clearTimeout(t);
      }
    } catch (_) { /* localStorage unavailable */ }
    return undefined;
  }, [forceOpen, autoStart, storageKey]);

  const step = steps[idx];

  useLayoutEffect(() => {
    if (!visible || !step) return undefined;
    let cancelled = false;
    let tries = 0;

    const measure = () => {
      if (cancelled) return;
      const el = document.querySelector(step.target);
      if (!el) {
        if (tries++ < 10) {
          setTimeout(measure, 200);
        } else {
          setIdx((i) => Math.min(steps.length - 1, i + 1));
        }
        return;
      }
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const r = el.getBoundingClientRect();
      const targetRect = {
        top: r.top - highlightPad,
        left: r.left - highlightPad,
        width: r.width + highlightPad * 2,
        height: r.height + highlightPad * 2,
      };
      setRect(targetRect);

      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let top = targetRect.top + targetRect.height + cardMargin;
      let left = targetRect.left;
      const approxHeight = 200;
      if (step.placement === 'top' || top + approxHeight > vh - 20) {
        top = targetRect.top - approxHeight - cardMargin;
      }
      if (top < 20) top = 20;
      if (left + cardWidth > vw - 20) left = vw - cardWidth - 20;
      if (left < 20) left = 20;
      setCardPos({ top, left });
    };

    const t = setTimeout(measure, 60);
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, { passive: true });
    return () => {
      cancelled = true;
      clearTimeout(t);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize);
    };
  }, [visible, idx, step, steps.length]);

  const close = (persist = true) => {
    setVisible(false);
    if (persist) {
      try { window.localStorage.setItem(storageKey, '1'); } catch (_) { /* ignore */ }
    }
    onFinish && onFinish();
  };

  if (!visible || !step) return null;

  const isFirst = idx === 0;
  const isLast = idx === steps.length - 1;

  return (
    <div
      className="fixed inset-0 z-[100] pointer-events-none"
      role="dialog"
      aria-label="Page tour"
      data-testid={`page-tour-${storageKey}`}
    >
      <div className="absolute inset-0 bg-navy/70 pointer-events-auto" onClick={() => close(true)} />
      {rect && (
        <div
          className="absolute rounded-2xl border-2 border-primary shadow-[0_0_0_9999px_rgba(10,31,51,0.72)] transition-all duration-300 pointer-events-none"
          style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }}
          data-testid="page-tour-highlight"
        />
      )}

      <div
        ref={cardRef}
        className="absolute pointer-events-auto bg-card border border-border rounded-2xl shadow-2xl overflow-hidden"
        style={{ top: cardPos.top, left: cardPos.left, width: cardWidth }}
        data-testid="page-tour-card"
      >
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 rounded-full bg-primary/15 text-primary flex items-center justify-center">
              <Sparkles className="w-3 h-3" />
            </div>
            <span className="eyebrow">Step {idx + 1} of {steps.length}</span>
          </div>
          <button
            type="button"
            onClick={() => close(true)}
            className="text-muted-foreground hover:text-foreground p-1 rounded-md"
            aria-label="Close tour"
            data-testid="page-tour-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-4 pb-3">
          <div className="font-heading text-base font-extrabold tracking-tight text-foreground">
            {step.title}
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed mt-1">{step.body}</p>
        </div>
        <div className="px-4 flex items-center gap-1.5">
          {steps.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-6 bg-primary' : 'w-1.5 bg-muted-foreground/30'}`}
            />
          ))}
        </div>
        <div className="px-4 py-3 flex items-center justify-between border-t border-border mt-3 bg-muted/20">
          <button
            type="button"
            onClick={() => close(true)}
            className="text-xs font-semibold text-muted-foreground hover:text-foreground"
            data-testid="page-tour-skip"
          >
            Skip tour
          </button>
          <div className="flex items-center gap-2">
            {!isFirst && (
              <button
                type="button"
                onClick={() => setIdx((i) => Math.max(0, i - 1))}
                className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted transition-colors"
                data-testid="page-tour-back"
              >
                <ArrowLeft className="w-3 h-3" /> Back
              </button>
            )}
            {isLast ? (
              <button
                type="button"
                onClick={() => close(true)}
                className="inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-4 py-1.5 text-xs font-extrabold hover:opacity-90 transition-opacity"
                data-testid="page-tour-finish"
              >
                Got it <Sparkles className="w-3 h-3" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setIdx((i) => Math.min(steps.length - 1, i + 1))}
                className="inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-4 py-1.5 text-xs font-extrabold hover:opacity-90 transition-opacity"
                data-testid="page-tour-next"
              >
                Next <ArrowRight className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Reusable "Take the tour" pill for the top of any page.
export const TourLaunchButton = ({ onClick, label = 'Take the tour', testId = 'tour-launch' }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/5 text-primary px-3 h-8 text-xs font-bold hover:bg-primary/10 transition-colors"
  >
    <Sparkles className="w-3.5 h-3.5" /> {label}
  </button>
);

export default PageTour;
